import React, { useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Stack,
  Button,
  IconButton,
  Tooltip,
  Slider,
  Chip,
  CircularProgress,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TimerIcon from '@mui/icons-material/Timer';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DownloadIcon from '@mui/icons-material/Download';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import LockIcon from '@mui/icons-material/Lock';
import { GamePeriod, GoalEventData, AudioWaveformSummary, StoryboardMetadata, TimelineEventItem } from '../../../types';
import { isGoalByJets } from '../../../utils/gameScoreUtils';
import { StoryboardPreview } from './StoryboardPreview';

export interface PackedPeriodRegion {
  period: GamePeriod;
  row: number;
  startPx: number;
  endPx: number;
}

export function packPeriodRegions(
  periods: GamePeriod[],
  timeToPx: (t: number) => number,
  activePeriodNum?: number | null,
  preferredRowMap?: Record<number, number>,
  minPaddingPx: number = 0
): { packed: PackedPeriodRegion[]; totalRows: number; rowMap: Record<number, number> } {
  if (!periods || periods.length === 0) {
    return { packed: [], totalRows: 1, rowMap: {} };
  }

  const prepared = periods.map((p) => {
    const startPx = timeToPx(p.startTime);
    const endPx = Math.max(startPx + 28, timeToPx(p.endTime));
    return { period: p, startPx, endPx };
  });

  // Sort so activePeriodNum is processed first.
  // This guarantees that the period being actively modified retains its preferred row,
  // and any conflicting non-active periods flow to the row below.
  prepared.sort((a, b) => {
    const aIsActive = activePeriodNum != null && a.period.period === activePeriodNum;
    const bIsActive = activePeriodNum != null && b.period.period === activePeriodNum;
    if (aIsActive && !bIsActive) return -1;
    if (!aIsActive && bIsActive) return 1;
    return a.period.period - b.period.period || a.startPx - b.startPx;
  });

  const rows: Array<Array<{ startPx: number; endPx: number }>> = [];
  const packed: PackedPeriodRegion[] = [];
  const nextRowMap: Record<number, number> = {};

  for (const item of prepared) {
    const isActive = activePeriodNum != null && item.period.period === activePeriodNum;
    const prefRow = preferredRowMap?.[item.period.period] ?? 0;
    let assignedRow = -1;

    if (isActive) {
      assignedRow = prefRow;
      while (rows.length <= assignedRow) {
        rows.push([]);
      }
      rows[assignedRow].push({ startPx: item.startPx, endPx: item.endPx });
    } else {
      // Try prefRow first if it doesn't collide
      if (prefRow < rows.length) {
        const collidesOnPref = rows[prefRow].some(
          (placed) => item.startPx < placed.endPx + minPaddingPx && placed.startPx < item.endPx + minPaddingPx
        );
        if (!collidesOnPref) {
          assignedRow = prefRow;
          rows[prefRow].push({ startPx: item.startPx, endPx: item.endPx });
        }
      }

      // If it collides on prefRow, find the lowest available row where it doesn't collide
      if (assignedRow === -1) {
        for (let r = 0; r < rows.length; r++) {
          const collides = rows[r].some(
            (placed) => item.startPx < placed.endPx + minPaddingPx && placed.startPx < item.endPx + minPaddingPx
          );
          if (!collides) {
            assignedRow = r;
            rows[r].push({ startPx: item.startPx, endPx: item.endPx });
            break;
          }
        }
      }

      // If still not assigned, open a new row below
      if (assignedRow === -1) {
        assignedRow = rows.length;
        rows.push([{ startPx: item.startPx, endPx: item.endPx }]);
      }
    }

    packed.push({
      period: item.period,
      row: assignedRow,
      startPx: item.startPx,
      endPx: item.endPx,
    });
    nextRowMap[item.period.period] = assignedRow;
  }

  // Ensure deterministic output order by period number
  packed.sort((a, b) => a.period.period - b.period.period);

  return { packed, totalRows: Math.max(1, rows.length), rowMap: nextRowMap };
}

export interface PackedTimelineEvent {
  event: TimelineEventItem;
  row: number;
  startPx: number;
  endPx: number;
}

export function packTimelineEvents(
  items: TimelineEventItem[],
  timeToPx: (t: number) => number,
  minPaddingPx: number = 16,
  pinnedRowMap?: Record<string, number>
): { packed: PackedTimelineEvent[]; totalRows: number } {
  if (!items || items.length === 0) {
    return { packed: [], totalRows: 1 };
  }

  // Pre-calculate visual bounds in pixel space
  const prepared = items.map((item) => {
    let startPx = 0;
    let endPx = 0;
    if (item.eventType === 'note') {
      const centerPx = timeToPx(item.time);
      startPx = centerPx - 10;
      endPx = centerPx + 10;
    } else {
      startPx = timeToPx(item.startTime);
      endPx = Math.max(startPx + 24, timeToPx(item.endTime));
    }
    return { item, startPx, endPx };
  });

  // Sort by start position in pixel space
  prepared.sort((a, b) => a.startPx - b.startPx);

  const rowEnds: number[] = [];
  const packed: PackedTimelineEvent[] = [];

  for (const { item, startPx, endPx } of prepared) {
    let assignedRow = -1;

    if (pinnedRowMap && pinnedRowMap[item.id] !== undefined) {
      assignedRow = pinnedRowMap[item.id];
      while (rowEnds.length <= assignedRow) {
        rowEnds.push(-Infinity);
      }
      rowEnds[assignedRow] = Math.max(rowEnds[assignedRow], endPx);
    } else {
      for (let r = 0; r < rowEnds.length; r++) {
        if (rowEnds[r] + minPaddingPx <= startPx) {
          assignedRow = r;
          rowEnds[r] = endPx;
          break;
        }
      }
      if (assignedRow === -1) {
        assignedRow = rowEnds.length;
        rowEnds.push(endPx);
      }
    }

    packed.push({
      event: item,
      row: assignedRow,
      startPx,
      endPx,
    });
  }

  return { packed, totalRows: Math.max(1, rowEnds.length) };
}

export interface PackedGoalRegion {
  goal: GoalEventData;
  row: number;
  startPx: number;
  endPx: number;
  startSec: number;
  endSec: number;
}

export function packGoalRegions(
  goals: GoalEventData[],
  highlights: any[] = [],
  timeToPx: (t: number) => number,
  minPaddingPx: number = 8
): { packed: PackedGoalRegion[]; totalRows: number } {
  if (!goals || goals.length === 0) {
    return { packed: [], totalRows: 1 };
  }

  const prepared = goals.map((g) => {
    const gId = g.eventId || g.id;
    const gh = (highlights || []).find((h: any) =>
      gId && h.eventId
        ? h.eventId === gId
        : h.goalNumber === g.goalNumber && Math.abs((h.videoTimestamp ?? g.videoTimestamp ?? 0) - (g.videoTimestamp ?? 0)) <= 35
    );
    const startSec = g.clipStartSeconds !== undefined
      ? g.clipStartSeconds
      : (gh?.clipStartSeconds !== undefined ? gh.clipStartSeconds : Math.max(0, (g.videoTimestamp || 0) - 15));
    const endSec = g.clipEndSeconds !== undefined
      ? g.clipEndSeconds
      : (gh?.clipEndSeconds !== undefined ? gh.clipEndSeconds : Math.max(startSec + 5, (g.videoTimestamp || 0) + 5));

    const startPx = timeToPx(startSec);
    const endPx = Math.max(startPx + 36, timeToPx(endSec));
    return { goal: g, startPx, endPx, startSec, endSec };
  });

  prepared.sort((a, b) => a.startPx - b.startPx);

  const rowEnds: number[] = [];
  const packed: PackedGoalRegion[] = [];

  for (const item of prepared) {
    let assignedRow = -1;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] + minPaddingPx <= item.startPx) {
        assignedRow = r;
        rowEnds[r] = item.endPx;
        break;
      }
    }
    if (assignedRow === -1) {
      assignedRow = rowEnds.length;
      rowEnds.push(item.endPx);
    }
    packed.push({
      goal: item.goal,
      row: assignedRow,
      startPx: item.startPx,
      endPx: item.endPx,
      startSec: item.startSec,
      endSec: item.endSec,
    });
  }

  return { packed, totalRows: Math.max(1, rowEnds.length) };
}

export interface FullWidthTimelineProps {
  gameId?: string;
  selectedFeed?: 'auto' | 'pano';
  feedOffset?: number;
  storyboardMetadata?: StoryboardMetadata | null;
  videoAspectRatio?: number | null;
  authToken?: string | null;
  timelineHeight: number;
  currentTime: number;
  duration: number;
  hasClipMark?: boolean;
  rangeClipStart?: number;
  rangeClipEnd?: number;
  rangeClipDuration?: number;
  clipIn?: number | null;
  clipOut?: number | null;
  onSetIn?: () => void;
  onSetOut?: () => void;
  isPlaying: boolean;
  onTogglePlay: () => void;
  isMuted?: boolean;
  onToggleMute?: () => void;
  onDownloadRangeClip?: (start: number, end: number) => void;
  downloadingRangeClip?: boolean;
  onToggleClipMark?: () => void;
  onRecenterClip?: () => void;
  onClearClip?: () => void;
  timelineZoom: number;
  onTimelineZoom: (zoom: number) => void;
  autoScrollTimeline: boolean;
  onToggleAutoScroll: () => void;
  timelineScrollRef: React.RefObject<HTMLDivElement | null>;
  trackWidth: number;
  timeToPx: (time: number) => number;
  onTimelineWheel: (e: React.WheelEvent<HTMLDivElement>) => void;
  getTimeFromClientX: (clientX: number) => number;
  handleSeek: (time: number, isScrubbing?: boolean) => void;
  renderRulerTicks: () => React.ReactNode;
  renderPeriodDividers?: () => React.ReactNode;
  renderGoalMarkers?: () => React.ReactNode;
  activeTimelineDrag: 'playhead' | 'start' | 'end' | 'window' | 'period-start' | 'period-end' | 'goal' | 'goal-start' | 'goal-end' | 'goal-window' | 'clip-start' | 'clip-end' | 'clip-window' | 'note' | null;
  setActiveTimelineDrag: (drag: any) => void;
  dragStartX: React.MutableRefObject<number>;
  dragInitialIn: React.MutableRefObject<number>;
  dragInitialOut: React.MutableRefObject<number>;
  onContainerWidthChange?: (width: number) => void;

  // Multi-Track Studio Enhancements
  isJetsHome?: boolean;
  targetTeam?: string;
  periods?: GamePeriod[];
  activePeriodNum?: number | null;
  goals?: GoalEventData[];
  highlights?: any[];
  waveform?: AudioWaveformSummary | null;
  isLoadingWaveform?: boolean;
  selectedTimelineItem?:
    | { type: 'period'; periodNumber: number }
    | { type: 'goal'; goalNumber: number; eventId?: string }
    | { type: 'note'; id: string }
    | { type: 'clip'; id: string }
    | null;
  unlockedTimelineItem?: { type: 'period' | 'goal' | 'clip' | string; id: number | string } | null;
  unlockedTimelineItems?: Array<{ type: 'period' | 'goal' | 'clip' | string; id: number | string }>;
  onSaveAll?: () => Promise<void> | void;
  savingAll?: boolean;
  onSelectPeriod?: (periodNumber: number) => void;
  onSelectGoal?: (goalIdOrNumber: number | string) => void;
  canEditTimeline?: boolean;
  onStartDragPeriod?: (e: React.PointerEvent, periodNumber: number, handle: 'start' | 'end') => void;
  onStartDragGoal?: (e: React.PointerEvent, goalIdOrNumber: number | string, handle?: 'start' | 'end' | 'window') => void;
  onOpenAddGoal?: () => void;
  onOpenAddEvent?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  onUndo?: () => void;
  onRedo?: () => void;
  onJumpToPeriod?: (periodNumber: number) => void;
  onJumpToGoal?: (timestampSeconds: number) => void;

  // Notes & Clips Multi-Row Support
  timelineEvents?: TimelineEventItem[];
  onSelectNote?: (id: string) => void;
  onSelectClip?: (id: string) => void;
  onCreateNote?: () => void;
  onCreateClip?: () => void;
  onStartDragClip?: (e: React.PointerEvent, clipId: string, handle: 'start' | 'end' | 'window') => void;
  onStartDragNote?: (e: React.PointerEvent, noteId: string) => void;
}

const formatTimecode = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  const wholeSec = Math.floor(s);
  const dec = Math.floor((s - wholeSec) * 10);
  return `${m < 10 ? '0' : ''}${m}:${wholeSec < 10 ? '0' : ''}${wholeSec}.${dec}`;
};

const formatShortTime = (sec: number): string => {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s < 10 ? '0' : ''}${s}`;
};

export const FullWidthTimeline: React.FC<FullWidthTimelineProps> = ({
  gameId,
  selectedFeed = 'auto',
  feedOffset = 0,
  storyboardMetadata,
  videoAspectRatio,
  authToken,
  timelineHeight,
  currentTime,
  duration,
  hasClipMark,
  rangeClipStart,
  rangeClipEnd,
  rangeClipDuration,
  clipIn,
  clipOut,
  onSetIn,
  onSetOut,
  isPlaying,
  onTogglePlay,
  isMuted = false,
  onToggleMute,
  onDownloadRangeClip,
  downloadingRangeClip,
  onToggleClipMark,
  onRecenterClip,
  onClearClip,
  timelineZoom,
  onTimelineZoom,
  autoScrollTimeline,
  onToggleAutoScroll,
  timelineScrollRef,
  trackWidth,
  timeToPx,
  onTimelineWheel,
  getTimeFromClientX,
  handleSeek,
  renderRulerTicks,
  renderPeriodDividers,
  renderGoalMarkers,
  activeTimelineDrag,
  setActiveTimelineDrag,
  dragStartX,
  dragInitialIn,
  dragInitialOut,
  onContainerWidthChange,
  isJetsHome = true,
  targetTeam,
  periods = [],
  activePeriodNum,
  goals = [],
  highlights = [],
  waveform = null,
  isLoadingWaveform = false,
  selectedTimelineItem = null,
  unlockedTimelineItem = null,
  unlockedTimelineItems = [],
  onSaveAll,
  savingAll = false,
  onSelectPeriod,
  onSelectGoal,
  canEditTimeline = false,
  onStartDragPeriod,
  onStartDragGoal,
  onOpenAddGoal,
  onOpenAddEvent,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  onJumpToPeriod,
  onJumpToGoal,
  timelineEvents = [],
  onSelectNote,
  onSelectClip,
  onCreateNote,
  onCreateClip,
  onStartDragClip,
  onStartDragNote,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const unlockedItemsList = unlockedTimelineItems.length > 0
    ? unlockedTimelineItems
    : (unlockedTimelineItem ? [unlockedTimelineItem] : []);

  const isItemUnlocked = (type: string, id: number | string): boolean => {
    return unlockedItemsList.some((item) => item.type === type && String(item.id) === String(id));
  };

  // Dynamic pixel-space interval packing for Notes and Clips
  const { packed: packedEvents, totalRows: eventRows } = React.useMemo(() => {
    return packTimelineEvents(timelineEvents, timeToPx, 16);
  }, [timelineEvents, timeToPx]);

  // Dynamic pixel-space interval packing for Goals (represented as clip range pills)
  const { packed: packedGoals, totalRows: totalGoalRows } = React.useMemo(() => {
    return packGoalRegions(goals, highlights, timeToPx, 8);
  }, [goals, highlights, timeToPx]);

  // Multi-Row Period Region Packing for overlapping period edits
  const periodRowMapRef = useRef<Record<number, number>>({});

  const activePeriod =
    activePeriodNum !== undefined
      ? activePeriodNum
      : (activeTimelineDrag === 'period-start' || activeTimelineDrag === 'period-end'
        ? (selectedTimelineItem?.type === 'period' ? selectedTimelineItem.periodNumber : null)
        : (selectedTimelineItem?.type === 'period' && isItemUnlocked('period', selectedTimelineItem.periodNumber)
            ? selectedTimelineItem.periodNumber
            : null));

  const { packed: packedPeriods, totalRows: totalPeriodRows, rowMap: updatedPeriodRowMap } = React.useMemo(() => {
    return packPeriodRegions(periods, timeToPx, activePeriod, periodRowMapRef.current);
  }, [periods, timeToPx, activePeriod]);

  useEffect(() => {
    periodRowMapRef.current = updatedPeriodRowMap;
  }, [updatedPeriodRowMap]);

  const periodTrackHeight = totalPeriodRows > 1 ? (totalPeriodRows * 26 + 4) : 28;

  // Hover tracking for Storyboard preview
  const [hoverPosition, setHoverPosition] = React.useState<{ x: number; time: number } | null>(null);

  const handleTimelinePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!timelineScrollRef.current) return;
    const rect = timelineScrollRef.current.getBoundingClientRect();
    const relativeX = e.clientX - rect.left + timelineScrollRef.current.scrollLeft;
    const time = getTimeFromClientX(e.clientX);
    setHoverPosition({ x: relativeX, time });
  };

  const handleTimelinePointerLeave = () => {
    setHoverPosition(null);
  };

  // ResizeObserver for container measurement
  useEffect(() => {
    const el = timelineScrollRef.current;
    if (!el) return;
    if (el.clientWidth > 0 && onContainerWidthChange) {
      onContainerWidthChange(el.clientWidth);
    }
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.contentRect.width > 0 && onContainerWidthChange) {
          onContainerWidthChange(Math.floor(entry.contentRect.width));
        }
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [timelineScrollRef, onContainerWidthChange]);

  // Render audio waveform onto HTML5 canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = Math.max(1, trackWidth);
    const height = 36;
    ctx.clearRect(0, 0, width, height);

    if (!waveform || !waveform.peaks || waveform.peaks.length === 0) {
      // Subtle background placeholder
      ctx.fillStyle = 'rgba(255, 255, 255, 0.02)';
      ctx.fillRect(0, 0, width, height);
      return;
    }

    const peaks = waveform.peaks;
    const numSamples = peaks.length;
    const sampleRate = waveform.sampleRateHz || 2;
    const pxPerSample = width / numSamples;
    const barWidth = Math.max(1, Math.min(4, pxPerSample * 0.95));

    for (let i = 0; i < numSamples; i++) {
      const peakVal = peaks[i];
      if (peakVal <= 0.01) continue;

      const time = i / sampleRate;
      const x = timeToPx(time);
      if (x < -10 || x > width + 10) continue;

      const barHeight = Math.max(2, Math.min(height, peakVal * height));
      const y = height - barHeight;

      // Color based on peak intensity:
      // peakVal > 0.65: Red/hot spike (horns, goals, whistles)
      // peakVal > 0.35: Vibrant amber
      // peakVal > 0.15: Electric sky blue
      // Ambient floor: subtle slate cyan
      if (peakVal > 0.65) {
        ctx.fillStyle = '#ef4444';
      } else if (peakVal > 0.35) {
        ctx.fillStyle = '#f59e0b';
      } else if (peakVal > 0.15) {
        ctx.fillStyle = '#38bdf8';
      } else {
        ctx.fillStyle = 'rgba(56, 189, 248, 0.45)';
      }

      ctx.fillRect(x, y, barWidth, barHeight);
    }
  }, [waveform, trackWidth, timeToPx]);

  return (
    <Box
      sx={{
        height: timelineHeight,
        width: '100%',
        bgcolor: '#09090b',
        color: '#fff',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        px: { xs: 1, sm: 1.5 },
        py: 0.75,
        gap: 0.75,
      }}
    >
      {/* Timeline Header: Badges, In/Out Actions, Undo/Redo, Zoom Controls */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'nowrap',
          gap: 1,
          overflowX: 'auto',
          flexShrink: 0,
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {/* Left: Timecode Badges, Actions, Undo/Redo */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          {/* Current Position / Total Duration Badge */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.12)',
              px: 1.25,
              py: 0.3,
              borderRadius: 1.5,
              fontFamily: 'monospace',
              fontSize: '0.78rem',
              color: '#ffffff',
            }}
          >
            <ScheduleIcon sx={{ fontSize: 15, color: 'primary.light' }} />
            <span>
              Pos: <strong>{formatTimecode(currentTime)}</strong> ({currentTime.toFixed(1)}s) / {formatTimecode(duration)}
            </span>
          </Box>

          {/* Undo / Redo Toolbar */}
          {(onUndo || onRedo) && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {onUndo && (
                <Tooltip title="Undo last change (Ctrl+Z)">
                  <span>
                    <IconButton
                      aria-label="Undo"
                      size="small"
                      onClick={onUndo}
                      disabled={!canUndo}
                      sx={{
                        color: canUndo ? '#fff' : 'rgba(255,255,255,0.3)',
                        p: 0.4,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                      }}
                    >
                      <UndoIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {onRedo && (
                <Tooltip title="Redo last change (Ctrl+Y)">
                  <span>
                    <IconButton
                      aria-label="Redo"
                      size="small"
                      onClick={onRedo}
                      disabled={!canRedo}
                      sx={{
                        color: canRedo ? '#fff' : 'rgba(255,255,255,0.3)',
                        p: 0.4,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.12)',
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
                      }}
                    >
                      <RedoIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          )}

          {/* Multi-Item Save All / Lock All Action (when multiple edits are open) */}
          {unlockedItemsList.length > 1 && onSaveAll && (
            <Tooltip title={`Save and lock all ${unlockedItemsList.length} currently unlocked items`}>
              <span>
                <Button
                  data-testid="timeline-save-all-btn"
                  size="small"
                  variant="contained"
                  onClick={onSaveAll}
                  disabled={savingAll}
                  startIcon={<LockIcon sx={{ fontSize: '13px !important' }} />}
                  sx={{
                    fontSize: '0.72rem',
                    py: 0.2,
                    px: 1,
                    bgcolor: '#f59e0b',
                    color: '#000',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 1.5,
                    boxShadow: '0 0 10px rgba(245, 158, 11, 0.4)',
                    '&:hover': { bgcolor: '#d97706' },
                  }}
                >
                  Save All / Lock All ({unlockedItemsList.length})
                </Button>
              </span>
            </Tooltip>
          )}

          {/* Add Event Button (replaces + Goal and + Clip) */}
          {canEditTimeline && (onOpenAddEvent || onOpenAddGoal) && (
            <Tooltip title="Add Event at playhead (Goal, Period/OT, Custom Clip, Penalty, Save) (Hotkey: G)">
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                aria-label="Add Event at Playhead"
                startIcon={<BookmarkAddIcon sx={{ fontSize: '13px !important' }} />}
                onClick={onOpenAddEvent || onOpenAddGoal}
                sx={{
                  fontSize: '0.72rem',
                  py: 0.2,
                  px: 0.8,
                  color: '#fbbf24',
                  borderColor: 'rgba(251, 191, 36, 0.4)',
                  bgcolor: 'rgba(251, 191, 36, 0.08)',
                  textTransform: 'none',
                }}
              >
                + Event
              </Button>
            </Tooltip>
          )}

          {/* Add Note Button */}
          {onCreateNote && (
            <Tooltip title="Add note at current playhead (Hotkey: N)">
              <Button
                size="small"
                variant="outlined"
                startIcon={<StickyNote2Icon sx={{ fontSize: '13px !important' }} />}
                onClick={onCreateNote}
                sx={{
                  fontSize: '0.72rem',
                  py: 0.2,
                  px: 0.8,
                  color: '#f59e0b',
                  borderColor: 'rgba(245, 158, 11, 0.4)',
                  bgcolor: 'rgba(245, 158, 11, 0.08)',
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.16)', borderColor: '#f59e0b' },
                }}
              >
                + Note
              </Button>
            </Tooltip>
          )}

          {/* Active Download Region Badge */}
          {hasClipMark && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'rgba(245, 158, 11, 0.12)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                color: '#fbbf24',
                px: 1.25,
                py: 0.3,
                borderRadius: 1.5,
                fontFamily: 'monospace',
                fontSize: '0.78rem',
              }}
            >
              <TimerIcon sx={{ fontSize: 15, color: '#f59e0b' }} />
              <span>
                Download Region: <strong>{formatTimecode(rangeClipStart ?? 0)}</strong> → <strong>{formatTimecode(rangeClipEnd ?? 0)}</strong> ({(rangeClipDuration || 0).toFixed(1)}s)
              </span>
            </Box>
          )}

          {/* Preview Region Button (only when download region is active) */}
          {hasClipMark && onTogglePlay && (
            <Tooltip title={isPlaying ? 'Pause preview (Space)' : 'Preview region (Space)'}>
              <Button
                size="small"
                variant="outlined"
                color="info"
                startIcon={isPlaying ? <PauseIcon sx={{ fontSize: '14px !important' }} /> : <PlayArrowIcon sx={{ fontSize: '14px !important' }} />}
                onClick={onTogglePlay}
                sx={{
                  fontSize: '0.72rem',
                  py: 0.2,
                  px: 0.8,
                  bgcolor: 'rgba(56, 189, 248, 0.1)',
                  borderColor: 'rgba(56, 189, 248, 0.4)',
                  color: '#38bdf8',
                  textTransform: 'none',
                }}
              >
                {isPlaying ? 'Pause' : 'Preview Region'}
              </Button>
            </Tooltip>
          )}

          {/* Download Clip Button */}
          {hasClipMark && (
            <Button
              size="small"
              variant="contained"
              color="primary"
              startIcon={downloadingRangeClip ? <CircularProgress size={14} sx={{ color: '#000' }} /> : <DownloadIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() => onDownloadRangeClip && rangeClipStart !== undefined && rangeClipEnd !== undefined && onDownloadRangeClip(rangeClipStart, rangeClipEnd)}
              disabled={downloadingRangeClip || (rangeClipDuration ?? 0) < 0.5}
              sx={{
                fontSize: '0.72rem',
                py: 0.2,
                px: 1,
                fontWeight: 700,
                bgcolor: downloadingRangeClip ? '#fbbf24' : '#f59e0b',
                color: '#000',
                '&:hover': { bgcolor: '#fbbf24' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
              }}
            >
              {downloadingRangeClip ? 'Preparing...' : `Download Clip (${(rangeClipDuration || 0).toFixed(1)}s)`}
            </Button>
          )}


          {/* Re-center / Clear Region buttons */}
          {hasClipMark && (
            <>
              <Tooltip title="Re-center Region (30s around playhead)">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={onRecenterClip}
                  sx={{
                    color: '#ffb74d',
                    borderColor: '#ffb74d',
                    fontSize: '0.72rem',
                    py: 0.2,
                    px: 0.75,
                    minWidth: 'auto',
                    textTransform: 'none',
                  }}
                >
                  Re-center
                </Button>
              </Tooltip>
              <Tooltip title="Clear Region (Esc)">
                <Button
                  size="small"
                  variant="text"
                  color="inherit"
                  onClick={onClearClip}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.75)',
                    fontSize: '0.72rem',
                    py: 0.2,
                    px: 0.75,
                    minWidth: 'auto',
                    textTransform: 'none',
                  }}
                >
                  Clear
                </Button>
              </Tooltip>
            </>
          )}
        </Stack>

        {/* Right: Timeline Zoom Controls */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <Tooltip title="Zoom out timeline">
            <span>
              <IconButton
                size="small"
                sx={{ color: '#fff', p: 0.4 }}
                onClick={() => onTimelineZoom(timelineZoom - 0.5)}
                disabled={timelineZoom <= 1.0}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Slider
            size="small"
            value={timelineZoom}
            min={1.0}
            max={10.0}
            step={0.1}
            onChange={(_, val) => onTimelineZoom(val as number)}
            sx={{
              width: 70,
              color: '#38bdf8',
              '& .MuiSlider-thumb': { width: 10, height: 10 },
            }}
          />

          <Tooltip title="Zoom in timeline">
            <span>
              <IconButton
                size="small"
                sx={{ color: '#fff', p: 0.4 }}
                onClick={() => onTimelineZoom(timelineZoom + 0.5)}
                disabled={timelineZoom >= 10.0}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                onTimelineZoom(1.0);
                if (timelineScrollRef.current) timelineScrollRef.current.scrollLeft = 0;
              }}
              sx={{
                color: timelineZoom > 1.0 ? '#38bdf8' : 'rgba(255, 255, 255, 0.7)',
                borderColor: timelineZoom > 1.0 ? 'rgba(56, 189, 248, 0.4)' : 'rgba(255, 255, 255, 0.2)',
                fontSize: '0.68rem',
                minWidth: 'auto',
                px: 0.75,
                py: 0.1,
                textTransform: 'none',
              }}
            >
              Fit
            </Button>
            {[2, 5].map((preset) => (
              <Button
                key={preset}
                size="small"
                variant="outlined"
                onClick={() => onTimelineZoom(preset)}
                sx={{
                  color: timelineZoom === preset ? '#38bdf8' : 'rgba(255, 255, 255, 0.75)',
                  borderColor: timelineZoom === preset ? '#38bdf8' : 'rgba(255, 255, 255, 0.25)',
                  fontSize: '0.68rem',
                  minWidth: 'auto',
                  px: 0.75,
                  py: 0.1,
                  textTransform: 'none',
                }}
              >
                {preset}x
              </Button>
            ))}
          </Stack>

          <Typography
            variant="caption"
            sx={{
              color: '#38bdf8',
              minWidth: 32,
              fontWeight: 600,
              fontSize: '0.72rem',
              fontFamily: 'monospace',
            }}
          >
            {timelineZoom.toFixed(1)}x
          </Typography>

          <Tooltip title={autoScrollTimeline ? 'Auto-scroll is ON: Timeline follows playhead' : 'Auto-scroll is OFF'}>
            <Chip
              label="Auto-Scroll"
              size="small"
              onClick={onToggleAutoScroll}
              sx={{
                cursor: 'pointer',
                height: 20,
                fontSize: '0.68rem',
                bgcolor: autoScrollTimeline ? 'rgba(56, 189, 248, 0.2)' : 'rgba(255,255,255,0.06)',
                color: autoScrollTimeline ? '#38bdf8' : 'rgba(255, 255, 255, 0.75)',
                border: '1px solid',
                borderColor: autoScrollTimeline ? 'rgba(56, 189, 248, 0.4)' : 'rgba(255, 255, 255, 0.25)',
              }}
            />
          </Tooltip>
        </Stack>
      </Box>

      {/* Multi-Track Scrollable Timeline Viewport Container */}
      <Box
        ref={timelineScrollRef}
        onWheel={onTimelineWheel}
        onPointerMove={handleTimelinePointerMove}
        onPointerLeave={handleTimelinePointerLeave}
        sx={{
          flex: 1,
          width: '100%',
          overflowX: 'auto',
          overflowY: 'auto',
          bgcolor: '#000',
          borderRadius: 1.5,
          border: '1px solid rgba(255, 255, 255, 0.15)',
          position: 'relative',
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.3) transparent',
          '&::-webkit-scrollbar': { width: 6, height: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.3)', borderRadius: 3 },
        }}
      >
        {/* Track Content Stack */}
        <Box
          sx={{
            position: 'relative',
            width: `${trackWidth}px`,
            minWidth: '100%',
            userSelect: 'none',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* TRACK 0: Time Ruler & Playhead Header Area (Top) */}
          <Box
            onPointerDown={(e) => {
              const time = getTimeFromClientX(e.clientX);
              handleSeek(time);
              setActiveTimelineDrag('playhead');
            }}
            sx={{
              position: 'relative',
              width: '100%',
              bgcolor: '#141416',
              borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
              cursor: 'pointer',
              touchAction: 'none',
              flexShrink: 0,
            }}
          >
            {/* Playhead Cap Gutter (Cap glides here above tick marks) */}
            <Box
              sx={{
                width: '100%',
                height: 36,
                bgcolor: 'rgba(0, 0, 0, 0.35)',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                position: 'relative',
              }}
            />

            {/* Time Ruler Ticks (Unobscured below playhead cap) */}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: 22,
              }}
            >
              {renderRulerTicks()}
            </Box>
          </Box>

          {/* TRACK 1: Period Track (Continuous Regions & Edge Handles) */}
          <Box
            data-testid="timeline-period-track"
            sx={{
              position: 'relative',
              width: '100%',
              height: periodTrackHeight,
              bgcolor: '#0f172a',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              flexShrink: 0,
              overflow: 'visible',
              transition: 'height 0.15s ease',
            }}
          >
            {/* Legacy/Test Period Dividers Support (only rendered when no studio periods exist) */}
            {renderPeriodDividers && periods.length === 0 && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                {renderPeriodDividers()}
              </div>
            )}

            {/* Continuous Period Block Regions */}
            {packedPeriods.map(({ period: p, row, startPx, endPx }) => {
              const regionWidth = Math.max(28, endPx - startPx);
              const isSelected = selectedTimelineItem?.type === 'period' && selectedTimelineItem.periodNumber === p.period;
              const isUnlocked = Boolean(
                isItemUnlocked('period', p.period) ||
                p.isLocked === false
              );

              return (
                <Box
                  key={`period-region-${p.period}`}
                  data-testid={`period-region-${p.period}`}
                  data-row={row}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onSelectPeriod) onSelectPeriod(p.period);
                  }}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (onJumpToPeriod) {
                      onJumpToPeriod(p.period);
                    } else {
                      handleSeek(p.startTime);
                    }
                  }}
                  sx={{
                    position: 'absolute',
                    top: row * 26 + 2,
                    height: 24,
                    left: `${startPx}px`,
                    width: `${regionWidth}px`,
                    bgcolor: isSelected
                      ? (isUnlocked ? 'rgba(245, 158, 11, 0.28)' : 'rgba(56, 189, 248, 0.28)')
                      : (isUnlocked ? 'rgba(245, 158, 11, 0.15)' : 'rgba(30, 41, 59, 0.85)'),
                    border: isSelected
                      ? (isUnlocked ? '2px solid #f59e0b' : '2px solid #38bdf8')
                      : (isUnlocked ? '1.5px dashed #f59e0b' : '1px solid rgba(148, 163, 184, 0.35)'),
                    boxShadow: isSelected
                      ? (isUnlocked ? '0 0 10px rgba(245, 158, 11, 0.5)' : '0 0 10px rgba(56, 189, 248, 0.5)')
                      : 'none',
                    borderRadius: 1,
                    zIndex: isSelected ? 8 : (4 + row),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 0.75,
                    cursor: 'pointer',
                    userSelect: 'none',
                    transition: 'border-color 0.15s, background-color 0.15s',
                    '&:hover': {
                      bgcolor: isSelected ? 'rgba(56, 189, 248, 0.35)' : 'rgba(51, 65, 85, 0.9)',
                      borderColor: isUnlocked ? '#f59e0b' : '#38bdf8',
                    },
                  }}
                >
                  {/* Left Trim Handle (Admin) - 14px hit target centered on boundary, only active when unlocked */}
                  {canEditTimeline && onStartDragPeriod && isUnlocked && (
                    <Box
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        onStartDragPeriod(e, p.period, 'start');
                      }}
                      sx={{
                        position: 'absolute',
                        left: -7,
                        top: 0,
                        bottom: 0,
                        width: 14,
                        cursor: 'col-resize',
                        zIndex: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover .handle-bar, &:active .handle-bar': {
                          bgcolor: '#f59e0b',
                          boxShadow: '0 0 6px #f59e0b',
                        },
                      }}
                    >
                      <Box
                        className="handle-bar"
                        sx={{
                          width: 3,
                          height: '75%',
                          bgcolor: isSelected ? '#38bdf8' : 'rgba(255, 255, 255, 0.5)',
                          borderRadius: 1,
                          transition: 'background-color 0.15s, box-shadow 0.15s',
                        }}
                      />
                    </Box>
                  )}

                  {/* Period Label & Duration */}
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pointerEvents: 'none', overflow: 'hidden' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.68rem',
                        fontWeight: 700,
                        color: isSelected ? '#fff' : 'rgba(255, 255, 255, 0.9)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {p.label || `P${p.period}`}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: '0.62rem',
                        color: 'rgba(255, 255, 255, 0.55)',
                        fontFamily: 'monospace',
                        display: { xs: 'none', sm: 'inline' },
                        whiteSpace: 'nowrap',
                      }}
                    >
                      ({formatShortTime(p.endTime - p.startTime)})
                    </Typography>
                  </Stack>

                  {/* Right Trim Handle (Admin) - 14px hit target centered on boundary, only active when unlocked */}
                  {canEditTimeline && onStartDragPeriod && isUnlocked && (
                    <Box
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        onStartDragPeriod(e, p.period, 'end');
                      }}
                      sx={{
                        position: 'absolute',
                        right: -7,
                        top: 0,
                        bottom: 0,
                        width: 14,
                        cursor: 'col-resize',
                        zIndex: 14,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover .handle-bar, &:active .handle-bar': {
                          bgcolor: '#f59e0b',
                          boxShadow: '0 0 6px #f59e0b',
                        },
                      }}
                    >
                      <Box
                        className="handle-bar"
                        sx={{
                          width: 3,
                          height: '75%',
                          bgcolor: isSelected ? '#38bdf8' : 'rgba(255, 255, 255, 0.5)',
                          borderRadius: 1,
                          transition: 'background-color 0.15s, box-shadow 0.15s',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>

          {/* TRACK 2: Goal Track (Directly below periods, Interval-Packed Pills) */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: 28,
              height: Math.max(28, totalGoalRows * 26 + 4),
              bgcolor: '#111827',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              flexShrink: 0,
              overflow: 'visible',
              transition: 'height 0.15s ease',
            }}
          >
            {/* Legacy/Test Goal Markers Support (only rendered when no studio goals exist) */}
            {renderGoalMarkers && goals.length === 0 && (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none' }}>
                {renderGoalMarkers()}
              </div>
            )}

            {/* Interactive Goal Pills (Sized by Start and End Time) */}
            {packedGoals.map(({ goal: g, row, startPx, endPx, startSec, endSec }, gIdx) => {
              if (endSec <= 0 || (duration > 0 && startSec > duration)) return null;
              const topPx = row * 26 + 2;
              const goalWidth = Math.max(36, endPx - startPx);
              const isJets = isGoalByJets(g, isJetsHome, targetTeam);
              const gId = g.eventId || g.id;
              const goalTargetId = gId || g.goalNumber;
              const isSelected = Boolean(
                selectedTimelineItem?.type === 'goal' &&
                (selectedTimelineItem.eventId && gId
                  ? selectedTimelineItem.eventId === gId
                  : selectedTimelineItem.goalNumber === g.goalNumber)
              );
              const isUnlocked = Boolean(
                (gId && isItemUnlocked('goal', gId)) ||
                isItemUnlocked('goal', g.goalNumber) ||
                g.isLocked === false
              );
              const scorerInfo = g.scorer ? ` - ${g.scorerJersey ? `#${g.scorerJersey} ` : ''}${g.scorer}` : (g.scorerJersey ? ` - #${g.scorerJersey}` : '');

              return (
                <Tooltip
                  key={`goal-marker-${gId || `${g.goalNumber}-${gIdx}`}`}
                  title={`Goal ${g.goalNumber}: ${g.scoringTeam} (${g.scoreAfter || ''})${scorerInfo} (${formatShortTime(endSec - startSec)}) [${formatShortTime(startSec)} - ${formatShortTime(endSec)}]${isUnlocked && canEditTimeline ? ' (Unlocked - drag handles to trim, drag middle to shift)' : ''}`}
                  arrow
                >
                  <Box
                    data-testid={`timeline-goal-${g.goalNumber}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onSelectGoal) onSelectGoal(goalTargetId);
                    }}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (onJumpToGoal) onJumpToGoal(g.videoTimestamp || startSec);
                    }}
                    onPointerDown={(e) => {
                      if (canEditTimeline && onStartDragGoal && isUnlocked) {
                        e.stopPropagation();
                        onStartDragGoal(e, goalTargetId, 'window');
                      }
                    }}
                    sx={{
                      position: 'absolute',
                      top: `${topPx}px`,
                      left: `${startPx}px`,
                      width: `${goalWidth}px`,
                      height: 24,
                      zIndex: isSelected ? 24 : 14,
                      cursor: canEditTimeline && isUnlocked ? (activeTimelineDrag === 'goal-window' || activeTimelineDrag === 'goal' ? 'grabbing' : 'grab') : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      bgcolor: isSelected
                        ? (isUnlocked ? 'rgba(245, 158, 11, 0.4)' : (isJets ? 'rgba(2, 132, 199, 0.65)' : 'rgba(220, 38, 38, 0.65)'))
                        : (isUnlocked ? 'rgba(245, 158, 11, 0.25)' : (isJets ? 'rgba(2, 132, 199, 0.45)' : 'rgba(220, 38, 38, 0.45)')),
                      color: '#fff',
                      px: 0.75,
                      borderRadius: 1,
                      border: isSelected
                        ? (isUnlocked ? '2px solid #f59e0b' : (isJets ? '2px solid #38bdf8' : '2px solid #f87171'))
                        : (isUnlocked ? '1.5px dashed #f59e0b' : (isJets ? '1.5px solid #0284c7' : '1.5px solid #dc2626')),
                      boxShadow: isSelected
                        ? (isUnlocked ? '0 0 12px #f59e0b' : (isJets ? '0 0 10px #0284c7' : '0 0 10px #dc2626'))
                        : (isUnlocked ? '0 0 8px rgba(245, 158, 11, 0.6)' : '0 2px 6px rgba(0,0,0,0.6)'),
                      userSelect: 'none',
                      overflow: 'visible',
                      transition: 'border-color 0.15s, background-color 0.15s',
                      '&:hover': {
                        bgcolor: isSelected
                          ? (isJets ? 'rgba(2, 132, 199, 0.75)' : 'rgba(220, 38, 38, 0.75)')
                          : (isJets ? 'rgba(2, 132, 199, 0.55)' : 'rgba(220, 38, 38, 0.55)'),
                        borderColor: isUnlocked ? '#f59e0b' : (isJets ? '#38bdf8' : '#f87171'),
                        zIndex: 26,
                      },
                      '&:active': {
                        cursor: canEditTimeline && isUnlocked ? 'grabbing' : 'pointer',
                      },
                    }}
                  >
                    {/* Left Trim Handle when Unlocked */}
                    {isUnlocked && onStartDragGoal && (
                      <Box
                        data-testid={`goal-handle-start-${g.goalNumber}`}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          onStartDragGoal(e, goalTargetId, 'start');
                        }}
                        sx={{
                          position: 'absolute',
                          left: -6,
                          top: 0,
                          bottom: 0,
                          width: 12,
                          cursor: 'col-resize',
                          zIndex: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover .handle-bar, &:active .handle-bar': {
                            bgcolor: '#f59e0b',
                            boxShadow: '0 0 6px #f59e0b',
                          },
                        }}
                      >
                        <Box
                          className="handle-bar"
                          sx={{
                            width: 3,
                            height: '75%',
                            bgcolor: isSelected ? (isJets ? '#38bdf8' : '#f87171') : 'rgba(255, 255, 255, 0.7)',
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    )}

                    {/* Goal Content */}
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pointerEvents: 'none', overflow: 'hidden' }}>
                      <SportsHockeyIcon sx={{ fontSize: 12, color: isJets ? '#38bdf8' : '#fca5a5', flexShrink: 0 }} />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.66rem',
                          fontWeight: 700,
                          color: '#fff',
                          lineHeight: 1,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {g.scoreAfter || `G${g.goalNumber}`}
                      </Typography>
                      {goalWidth > 70 && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.58rem',
                            color: 'rgba(255, 255, 255, 0.7)',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ({formatShortTime(endSec - startSec)})
                        </Typography>
                      )}
                      {goalWidth > 110 && scorerInfo && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.6rem',
                            color: 'rgba(255, 255, 255, 0.85)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {scorerInfo}
                        </Typography>
                      )}
                    </Stack>

                    {/* Subtle puck/goal timestamp indicator inside the pill */}
                    {g.videoTimestamp >= startSec && g.videoTimestamp <= endSec && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${timeToPx(g.videoTimestamp) - startPx}px`,
                          top: 2,
                          bottom: 2,
                          width: '2px',
                          bgcolor: 'rgba(255, 255, 255, 0.45)',
                          borderRadius: '1px',
                          pointerEvents: 'none',
                        }}
                      />
                    )}

                    {/* Locked Indicator */}
                    {!isUnlocked && (
                      <LockIcon sx={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.45)', flexShrink: 0, ml: 0.5, pointerEvents: 'none' }} />
                    )}

                    {/* Right Trim Handle when Unlocked */}
                    {isUnlocked && onStartDragGoal && (
                      <Box
                        data-testid={`goal-handle-end-${g.goalNumber}`}
                        onPointerDown={(e) => {
                          e.stopPropagation();
                          onStartDragGoal(e, goalTargetId, 'end');
                        }}
                        sx={{
                          position: 'absolute',
                          right: -6,
                          top: 0,
                          bottom: 0,
                          width: 12,
                          cursor: 'col-resize',
                          zIndex: 30,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          '&:hover .handle-bar, &:active .handle-bar': {
                            bgcolor: '#f59e0b',
                            boxShadow: '0 0 6px #f59e0b',
                          },
                        }}
                      >
                        <Box
                          className="handle-bar"
                          sx={{
                            width: 3,
                            height: '75%',
                            bgcolor: isSelected ? (isJets ? '#38bdf8' : '#f87171') : 'rgba(255, 255, 255, 0.7)',
                            borderRadius: 1,
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* TRACK: Notes & Clips Multi-Row Lane (Interval-Packed) */}
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              minHeight: 32,
              height: Math.max(32, eventRows * 28 + 6),
              bgcolor: '#0b0f17',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              flexShrink: 0,
              overflow: 'visible',
              transition: 'height 0.15s ease',
            }}
          >
            {/* Packed Event Items */}
            {packedEvents.map(({ event, row, startPx, endPx }) => {
              const topPx = row * 28 + 4;
              if (event.eventType === 'note') {
                const isSelected = selectedTimelineItem?.type === 'note' && selectedTimelineItem.id === event.id;
                const isUnlocked = Boolean(
                  isItemUnlocked('note', event.id) ||
                  event.isLocked === false
                );
                const px = timeToPx(event.time);
                return (
                  <Tooltip
                    key={`timeline-note-${event.id}`}
                    title={`Note @ ${formatShortTime(event.time)}: ${event.text || '(empty)'} (${event.generalAccess === 'public' ? 'Public' : (event.shares && event.shares.length > 0 ? 'Shared' : 'Private')})${isUnlocked ? ' (Unlocked - drag to adjust)' : ' (Locked)'}`}
                    arrow
                  >
                    <Box
                      data-testid={`timeline-note-${event.id}`}
                      className={`keyframe-diamond${isSelected ? ' selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSelectNote) onSelectNote(event.id);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleSeek(event.time);
                      }}
                      onPointerDown={(e) => {
                        if (isUnlocked && onStartDragNote) {
                          e.stopPropagation();
                          onStartDragNote(e, event.id);
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        top: `${topPx + 12}px`,
                        left: `${px}px`,
                        transform: isSelected
                          ? 'translate(-50%, -50%) rotate(45deg) scale(1.35)'
                          : 'translate(-50%, -50%) rotate(45deg)',
                        width: 12,
                        height: 12,
                        bgcolor: isSelected ? '#38bdf8' : '#f59e0b',
                        border: isSelected
                          ? (isUnlocked ? '2px solid #f59e0b' : '2px solid #ffffff')
                          : (isUnlocked ? '2px solid #f59e0b' : '2px solid #ffffff'),
                        boxShadow: isSelected
                          ? (isUnlocked ? '0 0 12px #f59e0b' : '0 0 10px rgba(56, 189, 248, 0.9)')
                          : (isUnlocked ? '0 0 8px rgba(245, 158, 11, 0.8)' : '0 2px 4px rgba(0,0,0,0.6)'),
                        cursor: isUnlocked ? (activeTimelineDrag === 'note' ? 'grabbing' : 'grab') : 'pointer',
                        zIndex: isSelected ? 26 : 16,
                        transition: 'transform 0.15s, background-color 0.15s, box-shadow 0.15s',
                        '&:hover': {
                          transform: 'translate(-50%, -50%) rotate(45deg) scale(1.3)',
                          boxShadow: isUnlocked ? '0 0 12px rgba(245, 158, 11, 1)' : '0 0 8px rgba(245, 158, 11, 0.9)',
                          zIndex: 28,
                        },
                      }}
                    />
                  </Tooltip>
                );
              } else {
                const isSelected = selectedTimelineItem?.type === 'clip' && selectedTimelineItem.id === event.id;
                const isUnlocked = Boolean(
                  isItemUnlocked('clip', event.id) ||
                  event.isLocked === false
                );
                const clipWidth = Math.max(24, timeToPx(event.endTime) - timeToPx(event.startTime));
                const clipStartPx = timeToPx(event.startTime);

                return (
                  <Tooltip
                    key={`timeline-clip-${event.id}`}
                    title={`Clip: ${event.title || 'Untitled'} (${formatShortTime(event.endTime - event.startTime)}) [${formatShortTime(event.startTime)} - ${formatShortTime(event.endTime)}]${isUnlocked ? ' (Unlocked - drag to adjust)' : ' (Locked)'}`}
                    arrow
                  >
                    <Box
                      data-testid={`timeline-clip-${event.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onSelectClip) onSelectClip(event.id);
                      }}
                      onDoubleClick={(e) => {
                        e.stopPropagation();
                        handleSeek(event.startTime);
                      }}
                      onPointerDown={(e) => {
                        if (isUnlocked && onStartDragClip) {
                          e.stopPropagation();
                          onStartDragClip(e, event.id, 'window');
                        }
                      }}
                      sx={{
                        position: 'absolute',
                        top: `${topPx}px`,
                        left: `${clipStartPx}px`,
                        width: `${clipWidth}px`,
                        height: 24,
                        borderRadius: 1,
                        bgcolor: isSelected
                          ? (isUnlocked ? 'rgba(245, 158, 11, 0.32)' : 'rgba(56, 189, 248, 0.32)')
                          : (isUnlocked ? 'rgba(245, 158, 11, 0.18)' : 'rgba(14, 165, 233, 0.18)'),
                        border: isSelected
                          ? (isUnlocked ? '2px solid #f59e0b' : '2px solid #38bdf8')
                          : (isUnlocked ? '1.5px dashed #f59e0b' : '1px solid rgba(56, 189, 248, 0.5)'),
                        boxShadow: isSelected
                          ? (isUnlocked ? '0 0 10px rgba(245, 158, 11, 0.5)' : '0 0 10px rgba(56, 189, 248, 0.5)')
                          : '0 1px 4px rgba(0,0,0,0.4)',
                        cursor: isUnlocked ? 'grab' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 0.75,
                        zIndex: isSelected ? 24 : 14,
                        userSelect: 'none',
                        overflow: 'visible',
                        transition: 'border-color 0.15s, background-color 0.15s',
                        '&:hover': {
                          bgcolor: isSelected ? 'rgba(56, 189, 248, 0.4)' : 'rgba(14, 165, 233, 0.28)',
                          borderColor: isUnlocked ? '#f59e0b' : '#38bdf8',
                          zIndex: 26,
                        },
                        '&:active': {
                          cursor: isUnlocked ? 'grabbing' : 'pointer',
                        },
                      }}
                    >
                      {/* Left Trim Handle when Unlocked */}
                      {isUnlocked && onStartDragClip && (
                        <Box
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            onStartDragClip(e, event.id, 'start');
                          }}
                          sx={{
                            position: 'absolute',
                            left: -6,
                            top: 0,
                            bottom: 0,
                            width: 12,
                            cursor: 'col-resize',
                            zIndex: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover .handle-bar, &:active .handle-bar': {
                              bgcolor: '#f59e0b',
                              boxShadow: '0 0 6px #f59e0b',
                            },
                          }}
                        >
                          <Box
                            className="handle-bar"
                            sx={{
                              width: 3,
                              height: '75%',
                              bgcolor: isSelected ? '#38bdf8' : 'rgba(255, 255, 255, 0.7)',
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      )}

                      {/* Clip Title & Duration */}
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ pointerEvents: 'none', overflow: 'hidden' }}>
                        <ContentCutIcon sx={{ fontSize: 11, color: isSelected ? '#38bdf8' : '#7dd3fc', flexShrink: 0 }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.64rem',
                            fontWeight: 700,
                            color: '#fff',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {event.title || 'Clip'}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.58rem',
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontFamily: 'monospace',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          ({formatShortTime(event.endTime - event.startTime)})
                        </Typography>
                      </Stack>

                      {/* Locked Indicator */}
                      {!isUnlocked && (
                        <LockIcon sx={{ fontSize: 11, color: 'rgba(255, 255, 255, 0.45)', flexShrink: 0, ml: 0.5, pointerEvents: 'none' }} />
                      )}

                      {/* Right Trim Handle when Unlocked */}
                      {isUnlocked && onStartDragClip && (
                        <Box
                          onPointerDown={(e) => {
                            e.stopPropagation();
                            onStartDragClip(e, event.id, 'end');
                          }}
                          sx={{
                            position: 'absolute',
                            right: -6,
                            top: 0,
                            bottom: 0,
                            width: 12,
                            cursor: 'col-resize',
                            zIndex: 30,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover .handle-bar, &:active .handle-bar': {
                              bgcolor: '#f59e0b',
                              boxShadow: '0 0 6px #f59e0b',
                            },
                          }}
                        >
                          <Box
                            className="handle-bar"
                            sx={{
                              width: 3,
                              height: '75%',
                              bgcolor: isSelected ? '#38bdf8' : 'rgba(255, 255, 255, 0.7)',
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                );
              }
            })}
          </Box>


          {/* TRACK 3: Audio Waveform Track (iMovie-Style Peaks) */}
          <Box
            data-testid="timeline-audio-track"
            onPointerDown={(e) => {
              const time = getTimeFromClientX(e.clientX);
              handleSeek(time);
            }}
            sx={{
              position: 'relative',
              width: '100%',
              height: 36,
              bgcolor: '#09090b',
              borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
              cursor: 'pointer',
              flexShrink: 0,
              overflow: 'hidden',
            }}
          >
            {/* Audio Waveform Canvas */}
            <canvas
              ref={canvasRef}
              width={Math.max(1, trackWidth)}
              height={36}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: `${trackWidth}px`,
                height: 36,
                display: 'block',
                pointerEvents: 'none',
              }}
            />
          </Box>

          {/* TRACK 4: Filmstrip & Tactile Trim Track (Always rendered below all tracks) */}
          <Box
            data-testid="timeline-filmstrip-track"
            onPointerDown={(e) => {
              if (activeTimelineDrag) return;
              const time = getTimeFromClientX(e.clientX);
              handleSeek(time);
              setActiveTimelineDrag('playhead');
            }}
            sx={{
              position: 'relative',
              width: '100%',
              flex: 1,
              minHeight: 88,
              bgcolor: '#0a0a0c',
              backgroundImage: 'repeating-linear-gradient(90deg, rgba(255,255,255,0.03) 0px, rgba(255,255,255,0.03) 1px, transparent 1px, transparent 20px)',
              cursor: 'pointer',
              touchAction: 'none',
              overflow: 'visible',
            }}
          >
            {/* Storyboard Hover & Scrub Floating Thumbnail Preview (Always rendered below all tracks) */}
            {gameId && storyboardMetadata && (hoverPosition || activeTimelineDrag === 'playhead') && (() => {
              const targetX = activeTimelineDrag === 'playhead' ? timeToPx(currentTime) : (hoverPosition?.x ?? 0);
              const thumbWidth = Math.round(
                79 * (videoAspectRatio || (storyboardMetadata?.tileWidth && storyboardMetadata?.tileHeight
                  ? storyboardMetadata.tileWidth / storyboardMetadata.tileHeight
                  : (selectedFeed === 'pano' ? (3840 / 1536) : (16 / 9))))
              );
              const halfThumb = thumbWidth / 2;
              const minCenter = halfThumb + 6;
              const maxCenter = Math.max(minCenter, trackWidth - halfThumb - 6);
              const thumbCenterX = Math.max(minCenter, Math.min(maxCenter, targetX));
              // Calculate caret offset within the thumbnail so it points accurately to targetX even when thumbnail is clamped
              const caretX = Math.max(8, Math.min(thumbWidth - 8, targetX - thumbCenterX + halfThumb));

              return (
                <Box
                  data-testid="storyboard-thumbnail-container"
                  style={{ left: `${thumbCenterX}px`, top: 4 }}
                  sx={{
                    position: 'absolute',
                    transform: 'translateX(-50%)',
                    zIndex: 45,
                    pointerEvents: 'none',
                    filter: 'drop-shadow(0 6px 16px rgba(0,0,0,0.85))',
                  }}
                >
                  {/* Pointer notch pointing up toward the Audio Waveform & Playhead needle */}
                  <Box
                    data-testid="storyboard-caret"
                    style={{ left: `${caretX}px` }}
                    sx={{
                      position: 'absolute',
                      top: -6,
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '5px solid transparent',
                      borderRight: '5px solid transparent',
                      borderBottom: '6px solid #38bdf8',
                      filter: 'drop-shadow(0 -1px 2px rgba(0,0,0,0.6))',
                      zIndex: 2,
                    }}
                  />
                  <StoryboardPreview
                    gameId={gameId}
                    feed={selectedFeed || 'auto'}
                    time={Math.max(0, (activeTimelineDrag === 'playhead' ? currentTime : (hoverPosition?.time ?? currentTime)) + feedOffset)}
                    metadata={storyboardMetadata}
                    authToken={authToken}
                    width={thumbWidth}
                    height={79}
                    showTimecode={true}
                  />
                </Box>
              );
            })()}

            {/* Active Trim Mask & Window */}
            {hasClipMark && (
              <>
                {/* Left Dim Mask */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${Math.max(0, timeToPx(rangeClipStart ?? 0))}px`,
                    bgcolor: 'rgba(0, 0, 0, 0.68)',
                    backdropFilter: 'blur(1.5px)',
                    zIndex: 7,
                    pointerEvents: 'none',
                  }}
                />

                {/* Right Dim Mask */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${timeToPx(rangeClipEnd ?? 0)}px`,
                    right: 0,
                    bgcolor: 'rgba(0, 0, 0, 0.68)',
                    backdropFilter: 'blur(1.5px)',
                    zIndex: 7,
                    pointerEvents: 'none',
                  }}
                />

                {/* Draggable Trim Selection Window */}
                <Box
                  onPointerDown={(e) => {
                    e.stopPropagation();
                    setActiveTimelineDrag('window');
                    dragStartX.current = e.clientX;
                    dragInitialIn.current = rangeClipStart ?? 0;
                    dragInitialOut.current = rangeClipEnd ?? 0;
                  }}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${timeToPx(rangeClipStart ?? 0)}px`,
                    width: `${Math.max(4, timeToPx(rangeClipEnd ?? 0) - timeToPx(rangeClipStart ?? 0))}px`,
                    borderTop: '3px solid #f59e0b',
                    borderBottom: '3px solid #f59e0b',
                    bgcolor: 'rgba(245, 158, 11, 0.14)',
                    zIndex: 10,
                    cursor: activeTimelineDrag === 'window' ? 'grabbing' : 'grab',
                    '&:active': { bgcolor: 'rgba(245, 158, 11, 0.22)' },
                  }}
                >
                  {/* Left Trim Handle */}
                  <Box
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActiveTimelineDrag('start');
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      left: 0,
                      transform: 'translateX(-100%)',
                      width: 18,
                      bgcolor: '#f59e0b',
                      borderRadius: '6px 0 0 6px',
                      zIndex: 14,
                      cursor: 'ew-resize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
                      '&::before': {
                        content: '""',
                        width: 2,
                        height: 14,
                        bgcolor: '#000',
                        borderRadius: 1,
                      },
                    }}
                  />

                  {/* Right Trim Handle */}
                  <Box
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      setActiveTimelineDrag('end');
                    }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      bottom: 0,
                      right: 0,
                      transform: 'translateX(100%)',
                      width: 18,
                      bgcolor: '#f59e0b',
                      borderRadius: '0 6px 6px 0',
                      zIndex: 14,
                      cursor: 'ew-resize',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 8px rgba(245, 158, 11, 0.6)',
                      '&::before': {
                        content: '""',
                        width: 2,
                        height: 14,
                        bgcolor: '#000',
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              </>
            )}

          </Box>

          {/* Draggable Red Needle Playhead Spanning from Cap Above Ruler Down Across All Tracks */}
          <Box
            data-testid="playhead-needle"
            sx={{
              position: 'absolute',
              top: 14,
              bottom: 0,
              left: `${timeToPx(currentTime)}px`,
              transform: 'translateX(-50%)',
              width: 2,
              bgcolor: '#ef4444',
              zIndex: 35,
              pointerEvents: 'auto',
              cursor: activeTimelineDrag === 'playhead' ? 'grabbing' : 'ew-resize',
              boxShadow: '0 0 6px rgba(239, 68, 68, 0.8)',
              transition: 'background-color 0.15s, box-shadow 0.15s',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: -6,
                right: -6,
                cursor: activeTimelineDrag === 'playhead' ? 'grabbing' : 'ew-resize',
              },
            }}
            onPointerDown={(e) => {
              e.stopPropagation();
              setActiveTimelineDrag('playhead');
            }}
          >
            {/* Playhead Cap (Chevron grab head matching cal-filter clips microservice) */}
            <Box
              data-testid="playhead-cap"
              onPointerDown={(e) => {
                e.stopPropagation();
                setActiveTimelineDrag('playhead');
              }}
              sx={{
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: activeTimelineDrag === 'playhead'
                  ? 'translateX(-50%) scale(1.15)'
                  : 'translateX(-50%)',
                width: 22,
                height: 22,
                bgcolor: activeTimelineDrag === 'playhead' ? '#dc2626' : '#ef4444',
                clipPath: 'polygon(0% 0%, 100% 0%, 100% 65%, 50% 100%, 0% 65%)',
                cursor: activeTimelineDrag === 'playhead' ? 'grabbing' : 'grab',
                pointerEvents: 'auto',
                touchAction: 'none',
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                pt: '3px',
                filter: 'drop-shadow(0 2px 6px rgba(239, 68, 68, 0.6))',
                transition: 'transform 0.1s, background-color 0.1s, filter 0.1s',
                zIndex: 36,
                '&:hover': {
                  bgcolor: '#dc2626',
                  transform: 'translateX(-50%) scale(1.15)',
                  cursor: 'grab',
                },
                '&:active': {
                  cursor: 'grabbing',
                },
                '&:hover .playhead-time-tag, &:active .playhead-time-tag': {
                  opacity: 1,
                },
              }}
            >
              {/* Inner White Dot (matching cal-filter .playhead-cap-inner) */}
              <Box
                data-testid="playhead-cap-inner"
                sx={{
                  width: 5,
                  height: 5,
                  bgcolor: '#ffffff',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                }}
              />

              {/* Floating Time Tag above Cap (matching cal-filter .playhead-time-tag) */}
              <Typography
                data-testid="playhead-time-tag"
                className="playhead-time-tag"
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 'calc(100% + 3px)',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  bgcolor: 'rgba(15, 23, 42, 0.95)',
                  color: '#38bdf8',
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 1,
                  border: '1px solid rgba(56, 189, 248, 0.5)',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.6)',
                  fontSize: '0.66rem',
                  fontFamily: 'monospace',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  pointerEvents: 'none',
                  zIndex: 40,
                  opacity: activeTimelineDrag === 'playhead' ? 1 : 0.9,
                  transition: 'opacity 0.15s',
                }}
              >
                {formatTimecode(currentTime)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
